import * as React from "react";
import { CircleCheck, Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { formatAdvanceAmounts } from "@/lib/advance";
import { tripRoute } from "@/lib/trip";
import { ApiError, discardMyRequest, fetchAdvance, fetchLinkableAdvances } from "../../api";
import {
  Advance,
  ClaimMethod,
  TRAVEL_TYPE_LABEL,
  TravelType
} from "../../types";
import { formatDate } from "@/lib/datetime";

const describeAdvance = (a: Advance) =>
  `${tripRoute(a)} - ${formatAdvanceAmounts(a.amounts)} - paid ${a.paidAt ? formatDate(a.paidAt) : ""}`;

// "Did you already receive an advance for this trip?" Only advances raised
// for the same travel type AND the same claim method as this request are
// listed (a Domestic Fixed Allowance request offers Domestic Fixed
// Allowance advances, and so on) - an advance raised before claim method was
// an up-front choice matches either scheme (see
// requests_api.py's _advance_matches_claim_method); create_my_request
// enforces the same match server-side. Linking one auto-fills the wizard's
// Travel Details step from the advance's own travel-detail fields
// (department/country/locations/dates/flight) - never the amount, which is
// the advance amount, not what's being claimed. onChange's second argument
// carries the full Advance so the caller can read those fields; it's
// undefined when unlinking.
//
// Once linked, the advance is shown as a badge with an Unlink button instead
// of the picker. Unlinking releases it (the caller tells the server when a
// Draft already holds it) so it shows up in the picker again - here, or for
// another request. onChange may return a promise and resolve to false to
// refuse the change (e.g. the server said no); the field then stays as it
// was rather than showing a state the server doesn't have.
export function LinkAdvanceField({
  travelType,
  claimMethod,
  linkedAdvanceId,
  onChange,
  fillsTravelDetails = true,
  enabled,
  onToggleChange
}: {
  travelType: TravelType;
  claimMethod: ClaimMethod;
  linkedAdvanceId: string | null;
  onChange: (
    advanceId: string | null,
    advance?: Advance
  ) => void | boolean | Promise<void | boolean>;
  /** False when the request's travel details are already locked in (a Draft
   *  exists), so linking only records the advance - nothing is filled in. */
  fillsTravelDetails?: boolean;
  /** Whether the "Did you already receive a travel advance?" toggle is on,
   *  owned by the wizard (not this component) so it survives this field
   *  unmounting when the employee steps away and back - this field is
   *  rendered only while its own wizard step is showing (`step ===
   *  advanceStepIndex && ...`), so component-local toggle state used to
   *  reset to `!!linkedAdvanceId` on every remount while the wizard's own
   *  "block Next" copy of it (see onToggleChange) stayed stuck at whatever
   *  it was before, drifting out of sync with what the Switch visibly
   *  showed. */
  enabled: boolean;
  /** Fired whenever the toggle changes - lets the wizard block Next while
   *  it's on but nothing's been picked yet (toggled on, no
   *  linkedAdvanceId), rather than silently letting the employee move on
   *  having said yes to an advance they never actually selected. */
  onToggleChange: (enabled: boolean) => void;
}) {
  const [advances, setAdvances] = React.useState<Advance[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  // The linked advance's details when it isn't in the linkable list - it
  // drops out of that list the moment a Draft holds it, so a resumed or
  // re-visited wizard has to fetch it by id to describe it.
  const [known, setKnown] = React.useState<Advance | null>(null);
  // An advance the employee is about to discard a Draft to free up - set
  // when they click "Use this instead" on a locked row, cleared on
  // cancel/confirm. The confirm dialog targets this rather than acting
  // straight from the click, since discarding loses that Draft's bills and
  // trip fields, not just the advance link.
  const [discardTarget, setDiscardTarget] = React.useState<Advance | null>(null);
  const [discarding, setDiscarding] = React.useState(false);

  const loadAdvances = React.useCallback(() => {
    setLoading(true);
    return fetchLinkableAdvances(travelType, claimMethod)
      .then(setAdvances)
      .catch(() => setAdvances([]))
      .finally(() => setLoading(false));
  }, [travelType, claimMethod]);
  React.useEffect(() => {
    void loadAdvances();
  }, [loadAdvances]);

  // list_paid_for_user_including_own_drafts (requests_api.py's
  // linkable_advances) now also returns an advance linked to one of the
  // employee's OWN Drafts, not just genuinely unlinked ones - split those
  // back out here: `pickable` behaves exactly as this list always did,
  // `lockedInDraft` surfaces the conflict instead of silently hiding it
  // (see this file's own "Use this instead" row below).
  const pickable = advances.filter((a) => a.linkedRequestSummary?.status !== "Draft");
  const lockedInDraft = advances.filter((a) => a.linkedRequestSummary?.status === "Draft");

  const linked = linkedAdvanceId
    ? (advances.find((a) => a.id === linkedAdvanceId) ??
      (known?.id === linkedAdvanceId ? known : null))
    : null;
  React.useEffect(() => {
    if (!linkedAdvanceId || loading || linked) return;
    fetchAdvance(linkedAdvanceId)
      .then(setKnown)
      .catch(() => {});
  }, [linkedAdvanceId, loading, linked]);

  const typeLabel = TRAVEL_TYPE_LABEL[travelType];

  const pick = async (advanceId: string) => {
    const advance = pickable.find((a) => a.id === advanceId);
    setBusy(true);
    try {
      if ((await onChange(advanceId, advance)) !== false && advance)
        setKnown(advance);
    } finally {
      setBusy(false);
    }
  };

  // Returns whether the advance is now unlinked.
  const unlink = async (): Promise<boolean> => {
    setBusy(true);
    try {
      if ((await onChange(null)) === false) return false;
      setKnown(null);
      await loadAdvances(); // the released advance is linkable again
      return true;
    } finally {
      setBusy(false);
    }
  };

  // Discards the Draft currently holding `discardTarget`, which releases
  // the advance back to Paid/unlinked (discard_my_request already does
  // this via AdvanceStore().unlink() - see its own docstring), then
  // refreshes the list so the now-free advance shows up as a normal pick.
  // Does NOT also link it here - the employee still picks it from the
  // (now-updated) dropdown themselves, same as any other advance that just
  // became available; this only clears the thing blocking it.
  const discardAndFree = async () => {
    if (!discardTarget) return;
    const draftId = discardTarget.linkedRequestSummary?.id;
    if (!draftId) return;
    setDiscarding(true);
    try {
      await discardMyRequest(draftId);
      toast.success("Draft discarded - the advance is free to link now.");
      setDiscardTarget(null);
      await loadAdvances();
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Could not discard that Draft - check your connection and try again."
      );
    } finally {
      setDiscarding(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <div>
          <Label>
            Did you already receive a travel advance?
          </Label>
          <p className="text-xs text-muted-foreground">
            Link it here so it's on record with this trip.
          </p>
        </div>
        <Switch
          checked={enabled || !!linkedAdvanceId}
          disabled={busy}
          onCheckedChange={async (checked) => {
            if (checked) {
              onToggleChange(true);
            } else if (!linkedAdvanceId || (await unlink())) {
              onToggleChange(false);
            }
          }}
        />
      </div>

      {(enabled || linkedAdvanceId) && (
        <div className="space-y-1.5">
          {linkedAdvanceId ? (
            <>
              <div className="flex items-start justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
                <div className="flex min-w-0 items-start gap-2">
                  <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <p className="font-medium">Advance linked</p>
                    <p className="break-words text-xs text-muted-foreground">
                      {linked ? describeAdvance(linked) : "Loading advance…"}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => void unlink()}
                  disabled={busy}
                >
                  {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                  Unlink
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {fillsTravelDetails
                  ? "Travel details on the next step were filled in from this advance. "
                  : ""}
                Unlink it to free the advance for another request.
              </p>
            </>
          ) : (
            <>
              <Label>Which advance?</Label>
              {loading ? (
                <p className="text-sm text-muted-foreground">
                  Loading your advances…
                </p>
              ) : pickable.length === 0 && lockedInDraft.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  You have no Paid {typeLabel} advances available to link. Raise
                  one under My Reimbursements → Advances first.
                </p>
              ) : (
                <>
                  {pickable.length > 0 && (
                    <Select
                      value=""
                      onValueChange={(v) => void pick(v)}
                      disabled={busy}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select an advance" />
                      </SelectTrigger>
                      {/* w-full + a hard max-w cap the popper's width to the
                         viewport (it otherwise sizes to its widest item's
                         natural/unwrapped width, which on a phone is wider
                         than the screen and spills out past the dialog,
                         overlapping the Back/Next footer below it - see this
                         file's own "Use this instead" row for the same
                         break-words pattern applied to a plain div instead). */}
                      <SelectContent className="w-full max-w-[calc(100vw-3rem)]">
                        {pickable.map((a) => (
                          <SelectItem
                            key={a.id}
                            value={a.id}
                            // Item text defaults to a single unbroken line
                            // (SelectPrimitive.ItemText has no wrap styling
                            // of its own) - wrapped onto two lines instead of
                            // forcing the dropdown wider than the trigger.
                            className="[&>span:last-child]:block [&>span:last-child]:whitespace-normal"
                          >
                            <span className="block break-words font-medium leading-snug">
                              {tripRoute(a)}
                            </span>
                            <span className="block break-words text-xs text-muted-foreground">
                              {formatAdvanceAmounts(a.amounts)} · paid{" "}
                              {a.paidAt
                                ? formatDate(a.paidAt)
                                : "-"}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {/* An advance held by one of the employee's OWN Drafts -
                      not offered in the Select above (picking it there
                      would just fail server-side), shown instead with its
                      own explicit way out: discard that Draft to free it. */}
                  {lockedInDraft.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-start justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
                    >
                      <div className="flex min-w-0 items-start gap-2">
                        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                        <div className="min-w-0">
                          <p className="break-words font-medium">
                            {describeAdvance(a)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Already linked to a Draft request you haven't
                            submitted yet.
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => setDiscardTarget(a)}
                        disabled={busy}
                      >
                        Use this instead
                      </Button>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!discardTarget}
        onOpenChange={(open) => !open && setDiscardTarget(null)}
        title="Discard the other Draft?"
        description={
          discardTarget
            ? `This advance is linked to a Draft request you started earlier and haven't submitted. Discarding it will permanently delete that Draft - any trip details or bills on it - and free the advance to link here instead.`
            : undefined
        }
        confirmLabel="Discard Draft & use this advance"
        pendingLabel="Discarding…"
        onConfirm={() => void discardAndFree()}
        pending={discarding}
        destructive
      />
    </div>
  );
}
